import React, { useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import PageMeta from '@/components/common/PageMeta';
import { useCarModel, useUpdateCarModel, useToggleCarModelActive, useUploadCarModelImage, useDeleteCarModel } from '@/features/car-models/hooks/useCarModels';
import Badge from '@/components/ui/badge/Badge';
import Button from '@/components/ui/button/Button';
import { ModalWithSearchParam } from '@/components/ui/modal/ModalWithSearchParam';
import ImagePreview from '@/components/ui/images/ImagePreview';
import { useModalSearchParam } from '@/shared/hooks/useModalSearchParam';
import CarModelEditForm from '@/features/car-models/components/CarModelEditForm';
import { Loader } from '@/components/ui/loader/Loader';
import { useToast } from '@/components/ui/toast/Toast';
import DetailsCard from '@/components/common/DetailsCard';

const CarModelDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const { data: model, isLoading, error } = useCarModel(id!);
  const { isOpen, open, close } = useModalSearchParam({ paramName: 'edit' });
  const updateCarModel = useUpdateCarModel();
  const toggleActive = useToggleCarModelActive();
  const deleteCarModel = useDeleteCarModel();
  const uploadImage = useUploadCarModelImage();
  const formRef = useRef<{ handleClose: () => void; isDirty: () => boolean }>(null);

  const handleModalClose = useCallback(() => {
    const hasUnsavedChanges = formRef.current?.isDirty() || false;
    if (hasUnsavedChanges) {
      formRef.current?.handleClose();
    } else {
      close();
    }
  }, [close]);

  const handleFormClose = useCallback((hasUnsavedChanges?: boolean) => {
    if (!hasUnsavedChanges) {
      close();
    }
  }, [close]);

  const handleUpdateModel = async (data: any, image?: File) => {
    if (!model) return;

    try {
      let imageUrl = model.image_url;

      if (image) {
        const uploadResult = await uploadImage.mutateAsync({
          id: model.id,
          file: image
        });
        imageUrl = uploadResult.image_url;
      }

      await updateCarModel.mutateAsync({
        id: model.id,
        data: {
          ...data,
          image_url: imageUrl
        }
      });

      close();
    } catch (error) {
      throw error;
    }
  };

  const handleToggleActive = async () => {
    if (!model) return;

    try {
      await toggleActive.mutateAsync({
        id: id!,
        isActive: !model.is_active
      });
      toast.success(t('car_models.status_toggle.success'));
    } catch (error) {
      toast.error(t('car_models.status_toggle.error') + `<br/><br/>${error}`);
    }
  };

  const handleDeleteModel = async () => {
    if (!model) return;
    try {
      await deleteCarModel.mutateAsync(model.id);
      toast.success(t('car_models.delete.success'));
      window.location.href = '/models';
    } catch (error) {
      toast.error(t('car_models.delete.error') + `<br/><br/>${error}`);
    }
  };

  if (isLoading) {
    return <Loader className='h-full w-full mt-60' />;
  }

  if (error || !model) {
    return <div>{t('cars.details.errors.model_not_found')}</div>;
  }

  return (
    <>
      <PageMeta
        title={`${model.brand_name} ${model.model_name} | Admin.Drivitive`}
        description={t('car_models.details.title')}
      />
      <PageBreadcrumb
        items={[
          { label: t('nav.car_models'), path: '/models' },
          { label: `${model.brand_name} ${model.model_name}` }
        ]}
        actions={
          <div className="flex gap-3">
            <Button variant="primary" onClick={open}>
              {t('car_models.details.edit')}
            </Button>
            <Button
              variant={model.is_active ? 'outline' : 'primary'}
              onClick={handleToggleActive}
              disabled={toggleActive.isPending}
            >
              {toggleActive.isPending ? t('Loading...') : model.is_active ? t('car_models.details.deactivate') : t('car_models.details.activate')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteModel}
              disabled={toggleActive.isPending}
            >
              {toggleActive.isPending ? t('Loading...') : t('car_models.details.delete')}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-3 2xl:gap-7.5">
        <DetailsCard
          title={t('car_models.details.about')}
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Badge
                size="lg"
                color={model.is_active ? 'success' : 'error'}
                variant="light"
              >
                {model.is_active ? t('car_models.status.active') : t('car_models.status.inactive')}
              </Badge>
            </div>
            {model.image_url && (
              <div className="relative">
                <div className="overflow-hidden rounded-lg">
                  <ImagePreview imageUrl={model.image_url}>
                    <img
                      src={model.image_url}
                      alt={`${model.brand_name} ${model.model_name}`}
                      className="w-full border border-border-primary rounded-xl"
                    />
                  </ImagePreview>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Badge size="sm" color="primary">
                  {model.body_type.toUpperCase()}
                </Badge>
                <Badge size="sm" color="info">
                  {model.transmission_type.toUpperCase()}
                </Badge>
              </div>
              <div className="flex gap-2 text-sm text-gray-500">
                <span>{t('car_models.details.slug')}</span>
                <span className="font-medium text-text-primary">{model.slug}</span>
              </div>
            </div>
          </div>
        </DetailsCard>

        <DetailsCard
          title={t('car_models.details.english_description')}
        >
          <div className="p-6 pt-0">
            <p className="mt-1 font-normal text-text-primary">
              {model.description_en || t('car_models.details.no_description')}
            </p>
          </div>
        </DetailsCard>

        <DetailsCard
          title={t('car_models.details.french_description')}
        >
          <div className="p-6 pt-0">
            <p className="mt-1 font-normal text-text-primary">
              {model.description_fr || t('car_models.details.no_description')}
            </p>
          </div>
        </DetailsCard>
      </div>

      <ModalWithSearchParam
        paramName="edit"
        maxWidth="800px"
        className="p-6"
        onClose={handleModalClose}
      >
        <CarModelEditForm
          ref={formRef}
          onSubmit={handleUpdateModel}
          onClose={handleFormClose}
          initialData={{
            slug: model.slug,
            brand_name: model.brand_name,
            model_name: model.model_name,
            transmission_type: model.transmission_type,
            body_type: model.body_type,
            tech_profile: model.tech_profile,
            description_en: model.description_en,
            description_fr: model.description_fr,
            is_active: model.is_active,
            image_url: model.image_url
          }}
        />
      </ModalWithSearchParam>
    </>
  );
};

export default CarModelDetailsPage;
